/**
 * useZoneFocusCamera — fly the map to frame a single selected zone, top-down.
 *
 * Whenever `selectedZoneId` changes to a known zone, ease to pitch 0 with a
 * fixed default bearing (`FOCUS_BEARING_DEG`), framed tightly on the zone's
 * (rotation-aware) footprint. The bearing is a constant rather than derived from
 * the zone's placement heading so the framing is stable and immune to the scene-
 * rotation↔compass convention (deriving it left the view 180° off). Unlike
 * `useTopDownCamera` this is a one-shot fly-to — it never snapshots/restores, so
 * it composes with the top-down toggle.
 */
import { useEffect, useRef } from 'react'
import { CoordinateService } from '@/services/3d/CoordinateService'
import type { ZoneGeometry } from '../engine/types'

/** Compass bearing (deg) the focus view rotates "up" to — ~the KAGS apron line. */
const FOCUS_BEARING_DEG = 350

export function useZoneFocusCamera(
  map: mapboxgl.Map | null,
  selectedZoneId: string | null,
  zones: ZoneGeometry[],
  center: [number, number] | null
) {
  // Only refocus when the *selection* changes, not on every zone-settings edit.
  const lastFocused = useRef<string | null>(null)

  useEffect(() => {
    if (!map || !center || !selectedZoneId) return
    if (lastFocused.current === selectedZoneId) return
    const zone = zones.find((z) => z.areaId === selectedZoneId)
    if (!zone) return
    lastFocused.current = selectedZoneId

    // Rotation-aware footprint corners in scene feet, then projected to lng/lat.
    const hw = zone.width / 2
    const hd = zone.depth / 2
    const rad = (zone.rotationDeg * Math.PI) / 180
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)
    const offsets: [number, number][] = [
      [-hw, -hd],
      [hw, -hd],
      [hw, hd],
      [-hw, hd],
    ]

    let minLng = Infinity
    let minLat = Infinity
    let maxLng = -Infinity
    let maxLat = -Infinity
    for (const [dx, dz] of offsets) {
      const x = zone.center[0] + dx * cos - dz * sin
      const z = zone.center[1] + dx * sin + dz * cos
      const [lng, lat] = CoordinateService.scenePositionToLngLat([x, 0, z], center)
      minLng = Math.min(minLng, lng)
      maxLng = Math.max(maxLng, lng)
      minLat = Math.min(minLat, lat)
      maxLat = Math.max(maxLat, lat)
    }

    const bearing = FOCUS_BEARING_DEG
    const cam = map.cameraForBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { bearing, padding: 120 }
    )
    if (cam) {
      map.easeTo({ center: cam.center, zoom: cam.zoom, pitch: 0, bearing, duration: 600 })
    }
  }, [map, selectedZoneId, zones, center])
}
